import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { DollarSign, TrendingUp, Coffee, Users, Percent } from "lucide-react";
import { buildAdminApi } from "../../lib/admin";
import { toast } from "sonner";
import FirstPaymentCelebration from "../../components/FirstPaymentCelebration";
import LaunchChecklist from "../../components/LaunchChecklist";

const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AdminHome = () => {
    const { slug } = useParams();
    const adminApi = useMemo(() => buildAdminApi(slug), [slug]);
    const [today, setToday] = useState(null);
    const [seven, setSeven] = useState(null);
    const [stampsRequired, setStampsRequired] = useState(null);
    const [savingStamps, setSavingStamps] = useState(false);
    const [financials, setFinancials] = useState(null);

    useEffect(() => {
        const load = () => {
            adminApi.get("/admin/reports?period=today").then((r) => setToday(r.data));
            adminApi.get("/admin/reports?period=7d").then((r) => setSeven(r.data));
            adminApi.get("/admin/financials?period=30d").then((r) => setFinancials(r.data)).catch(() => {});
        };
        load();
        adminApi.get("/admin/settings").then((r) => {
            setStampsRequired(r.data.stamps_required || 10);
        });
        const int = setInterval(load, 30000);
        return () => clearInterval(int);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const saveStamps = async (n) => {
        const clamped = Math.max(2, Math.min(20, n));
        setStampsRequired(clamped);
        setSavingStamps(true);
        try {
            await adminApi.patch("/admin/settings", { stamps_required: clamped });
            toast.success(`Every ${clamped}th coffee is now free`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSavingStamps(false);
        }
    };

    return (
        <div>
            <FirstPaymentCelebration api={adminApi} surface="admin" cafeName={slug} />
            <LaunchChecklist api={adminApi} />
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
                <h1 className="font-display font-black text-3xl">Today at a glance</h1>
                <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                    Refreshes every 30s
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KPI label="Revenue" value={today ? `$${today.revenue.toFixed(2)}` : "—"} sub="today" icon={DollarSign} testid="kpi-revenue" />
                <KPI label="Orders" value={today ? today.orders : "—"} sub="today" icon={TrendingUp} testid="kpi-orders" />
                <KPI label="Coffees poured" value={today ? today.coffees : "—"} sub="stamps issued" icon={Coffee} testid="kpi-coffees" />
                <KPI label="Avg ticket" value={today ? `$${today.average_order.toFixed(2)}` : "—"} sub="per order" icon={Users} testid="kpi-aov" />
            </div>

            {/* Top items */}
            <div className="mt-8 rounded-3xl bg-white border border-[#E8DCCB] p-6">
                <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                    Top selling today
                </div>
                <div className="mt-3">
                    {today && today.top_items.length > 0 ? (
                        <div className="flex flex-col gap-2">
                            {today.top_items.slice(0, 5).map((item, idx) => (
                                <div
                                    key={item.name}
                                    className="flex items-center justify-between py-2 border-b border-[#E8DCCB]/60 last:border-0"
                                    data-testid={`top-${idx}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-[#F9F6F0] flex items-center justify-center font-display font-black text-sm">
                                            {idx + 1}
                                        </div>
                                        <div className="font-display font-bold">{item.name}</div>
                                    </div>
                                    <div className="font-display font-black text-lg">{item.qty}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-sm text-[#8C7A72] py-6 text-center">
                            No orders yet today. Share your link to get started.
                        </div>
                    )}
                </div>
            </div>

            {/* Week */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 rounded-3xl bg-[#2C1A12] text-white p-6 relative overflow-hidden"
            >
                <div className="absolute -right-8 -bottom-8 h-40 w-40 rounded-full bg-[#FF4F18]/30" />
                <div className="relative">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#FF4F18] font-bold">
                        Last 7 days
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-4">
                        <div>
                            <div className="font-display font-black text-3xl">
                                {seven ? `$${seven.revenue.toFixed(0)}` : "—"}
                            </div>
                            <div className="text-xs text-white/60 uppercase tracking-wider mt-1">
                                Revenue
                            </div>
                        </div>
                        <div>
                            <div className="font-display font-black text-3xl">
                                {seven ? seven.orders : "—"}
                            </div>
                            <div className="text-xs text-white/60 uppercase tracking-wider mt-1">
                                Orders
                            </div>
                        </div>
                        <div>
                            <div className="font-display font-black text-3xl">
                                {seven ? seven.unique_customers : "—"}
                            </div>
                            <div className="text-xs text-white/60 uppercase tracking-wider mt-1">
                                Customers
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Financials — plan cost + Coffeein7 platform fees */}
            {financials && (
                <div
                    className="mt-6 rounded-3xl bg-white border border-[#E8DCCB] p-6"
                    data-testid="financials-card"
                >
                    <div className="flex items-baseline justify-between flex-wrap gap-2">
                        <div>
                            <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                                Your plan · {financials.period.label}
                            </div>
                            <div className="mt-1 font-display font-black text-2xl">
                                {financials.plan.name}
                            </div>
                            <div className="mt-1 text-sm text-[#8C7A72]">
                                {money(financials.plan.monthly_subscription_cents)}/month subscription
                                · {financials.plan.coffeein7_fee_percent}% transaction fee
                            </div>
                        </div>
                        <div className="flex items-center gap-2 rounded-full bg-[#F9F6F0] px-3 py-1.5 border border-[#E8DCCB]">
                            <Percent className="h-3.5 w-3.5 text-[#FF4F18]" strokeWidth={2.5} />
                            <span className="text-xs font-display font-black uppercase tracking-wider text-[#2C1A12]">
                                {financials.plan.coffeein7_fee_percent}% fee
                            </span>
                        </div>
                    </div>

                    <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                        <MiniStat
                            label="Customer orders"
                            value={money(financials.totals.customer_cents)}
                            sub={`${financials.totals.orders} orders`}
                            testid="fin-gross"
                        />
                        <MiniStat
                            label="Coffeein7 fees"
                            value={money(financials.totals.fee_cents)}
                            sub={`at ${financials.plan.coffeein7_fee_percent}%`}
                            testid="fin-c7-fees"
                            accent
                        />
                        <MiniStat
                            label="Tips (barista)"
                            value={money(financials.totals.tip_cents)}
                            sub="not fee-eligible"
                            testid="fin-tips"
                        />
                        <MiniStat
                            label="Your net"
                            value={money(financials.totals.net_cents)}
                            sub="before Stripe fees"
                            testid="fin-net"
                            highlight
                        />
                    </div>

                    {/* Refunds line — only render if non-zero */}
                    {financials.totals.reversed_amount_cents > 0 && (
                        <div className="mt-4 text-sm text-[#8C7A72]">
                            Refunded to customers: <span className="font-bold text-[#2C1A12]">{money(financials.totals.reversed_amount_cents)}</span>
                            {" · "}Coffeein7 fees reversed: <span className="font-bold text-[#2C1A12]">{money(financials.totals.reversed_fee_cents)}</span>
                        </div>
                    )}

                    {/* Upgrade / downgrade comparison — factual, not sales-y */}
                    <UpgradeCompare comparison={financials.upgrade_comparison} />

                    <div className="mt-4 text-[11px] text-[#8C7A72] leading-relaxed">
                        The Coffeein7 fee applies only to orders paid online in the app and is deducted before payout — pay-at-counter orders are fee-free, and customers never see it on their receipt.
                        Stripe processing fees (~1.75% + $0.30 AU) are separate and apply only to online payments.
                    </div>
                </div>
            )}

            {/* Loyalty threshold — owner-configurable Nth coffee free */}
            <div
                className="mt-6 rounded-3xl bg-white border border-[#E8DCCB] p-6"
                data-testid="loyalty-settings-card"
            >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                            Loyalty program
                        </div>
                        <div className="font-display font-black text-xl mt-1">
                            Every{" "}
                            <span className="text-[#FF4F18]">
                                {stampsRequired ? `${stampsRequired}th` : "…"}
                            </span>{" "}
                            coffee is free
                        </div>
                        <div className="text-sm text-[#8C7A72] mt-1">
                            The industry standard is 10. Some cafés run 7 or 8 to feel more generous.
                            Customers see progress dots on the menu.
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                        {[7, 8, 10, 12].map((n) => (
                            <button
                                key={n}
                                data-testid={`stamps-${n}`}
                                disabled={savingStamps}
                                onClick={() => saveStamps(n)}
                                className={`h-11 min-w-[52px] px-3 rounded-full text-sm font-display font-black border-2 transition ${
                                    stampsRequired === n
                                        ? "bg-[#2C1A12] text-white border-[#2C1A12]"
                                        : "bg-white text-[#2C1A12] border-[#E8DCCB] hover:border-[#2C1A12]/50"
                                }`}
                            >
                                {n}
                            </button>
                        ))}
                        <input
                            data-testid="stamps-custom"
                            type="number"
                            min={2}
                            max={20}
                            value={stampsRequired ?? ""}
                            onChange={(e) => setStampsRequired(Number(e.target.value) || "")}
                            onBlur={(e) => {
                                const n = Number(e.target.value);
                                if (n && n !== (today ? stampsRequired : null)) saveStamps(n);
                            }}
                            className="h-11 w-16 rounded-full text-sm font-display font-black border-2 border-[#E8DCCB] bg-white text-center focus:border-[#FF4F18] outline-none"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const KPI = ({ label, value, sub, icon: Icon, testid }) => (
    <div className="rounded-3xl bg-white border border-[#E8DCCB] p-5 relative overflow-hidden" data-testid={testid}>
        <div className="absolute right-3 top-3 opacity-20">
            <Icon className="h-8 w-8" strokeWidth={1.5} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">{label}</div>
        <div className="mt-2 font-display font-black text-2xl">{value}</div>
        <div className="text-[10px] text-[#8C7A72] mt-1 uppercase tracking-wider">{sub}</div>
    </div>
);

const MiniStat = ({ label, value, sub, testid, accent = false, highlight = false }) => (
    <div
        className={`rounded-2xl p-4 border ${
            highlight
                ? "bg-[#2C1A12] text-white border-[#2C1A12]"
                : accent
                    ? "bg-[#FFF5F0] border-[#FF4F18]/30"
                    : "bg-[#F9F6F0] border-[#E8DCCB]"
        }`}
        data-testid={testid}
    >
        <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${highlight ? "text-white/60" : "text-[#8C7A72]"}`}>
            {label}
        </div>
        <div className="mt-1.5 font-display font-black text-xl">{value}</div>
        <div className={`text-[10px] mt-1 ${highlight ? "text-white/50" : "text-[#8C7A72]"}`}>{sub}</div>
    </div>
);

const UpgradeCompare = ({ comparison }) => {
    if (!comparison) return null;
    const { current, alternative, monthly_difference_cents } = comparison;
    // Positive difference means the alternative is CHEAPER (saves money).
    // Negative means it costs more. Zero = same.
    const saves = monthly_difference_cents > 0;
    const abs = Math.abs(monthly_difference_cents);
    const altName = alternative.plan === "pro" ? "Coffeein7 Pro ($79)" : "Coffeein7 Starter ($49)";
    return (
        <div
            className="mt-6 rounded-2xl bg-[#F9F6F0] border border-[#E8DCCB] p-4"
            data-testid="upgrade-compare"
        >
            <div className="text-xs uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">
                On your actual order volume this period
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-white p-3 border border-[#E8DCCB]">
                    <div className="text-[10px] uppercase tracking-wider text-[#8C7A72]">Current</div>
                    <div className="mt-1 font-display font-bold text-sm capitalize">{current.plan}</div>
                    <div className="text-[11px] text-[#8C7A72] mt-1">
                        Sub {`$${(current.subscription_cents / 100).toFixed(0)}`} + fees {`$${(current.coffeein7_fee_cents / 100).toFixed(2)}`}
                    </div>
                    <div className="mt-1 font-display font-black">
                        {`$${(current.total_cost_cents / 100).toFixed(2)}`}/mo
                    </div>
                </div>
                <div className="rounded-xl bg-white p-3 border border-[#E8DCCB]">
                    <div className="text-[10px] uppercase tracking-wider text-[#8C7A72]">If on {alternative.plan}</div>
                    <div className="mt-1 font-display font-bold text-sm capitalize">{alternative.plan}</div>
                    <div className="text-[11px] text-[#8C7A72] mt-1">
                        Sub {`$${(alternative.subscription_cents / 100).toFixed(0)}`} + fees {`$${(alternative.coffeein7_fee_cents / 100).toFixed(2)}`}
                    </div>
                    <div className="mt-1 font-display font-black">
                        {`$${(alternative.total_cost_cents / 100).toFixed(2)}`}/mo
                    </div>
                </div>
            </div>
            {abs > 0 && (
                <div
                    className={`mt-3 text-sm font-display font-bold ${
                        saves ? "text-[#0E6B4C]" : "text-[#8C7A72]"
                    }`}
                    data-testid="upgrade-verdict"
                >
                    {saves
                        ? `${altName} would save you ~$${(abs / 100).toFixed(2)}/month`
                        : `${altName} would cost ~$${(abs / 100).toFixed(2)}/month more`}
                </div>
            )}
        </div>
    );
};

export default AdminHome;
